import React, { useState } from 'react';
import { Crown, GraduationCap, BookOpen, ArrowLeft, Loader2, Repeat } from 'lucide-react';
import { authAPI } from '../api';
import { useAuth } from '../AuthContext';
import { toast } from 'sonner';

// Super admin "switch view": use the site as a teacher or a student with the
// same account, then come back. The server checks the real account each time.
const DASHBOARD = { admin: '/dashboard/admin', tutor: '/dashboard/tutor', student: '/dashboard/student' };
const LABEL = { admin: 'Super Admin', tutor: 'Teacher', student: 'Student' };
const ROLE_LABEL = { admin: 'Admin', tutor: 'Teacher', student: 'Student' };

export const useSwitchView = () => {
  const { loginWithToken, user } = useAuth();
  const [switching, setSwitching] = useState('');
  const switchTo = async (role) => {
    setSwitching(role);
    try {
      const { data } = await authAPI.actAs(role);
      const primary = data.user?.primary_role || user?.primary_role || 'admin';
      try {
        if (role === primary) localStorage.removeItem('bge_acting_as');
        else localStorage.setItem('bge_acting_as', role);
      } catch { /* private mode */ }
      loginWithToken(data.access_token, data.user);
      // Full load so every page and guard starts fresh with the new view.
      window.location.assign(DASHBOARD[role]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not switch view');
      setSwitching('');
    }
  };
  return { switchTo, switching };
};

// Buttons on the admin dashboard (super admin only).
export const ViewSwitcher = () => {
  const { switchTo, switching } = useSwitchView();
  return (
    <div className="bg-white rounded-2xl shadow-card p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="view-switcher">
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-text-primary flex items-center gap-2">
          <Crown className="w-4 h-4 text-amber-600" /> Switch view
        </p>
        <p className="text-sm text-text-secondary">Use the site as a teacher or a student with your own account. You can switch back any time.</p>
      </div>
      <div className="flex gap-2 flex-shrink-0">
        {[['tutor', BookOpen], ['student', GraduationCap]].map(([role, Icon]) => (
          <button
            key={role}
            type="button"
            disabled={Boolean(switching)}
            onClick={() => switchTo(role)}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-text-primary hover:border-primary hover:bg-primary/5 transition-colors"
            data-testid={`view-as-${role}`}
          >
            {switching === role ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4 text-primary" />}
            {LABEL[role]}
          </button>
        ))}
      </div>
    </div>
  );
};

// Bar shown on every page while someone uses a role other than their main one.
export const ActingBanner = () => {
  const { user } = useAuth();
  const { switchTo, switching } = useSwitchView();
  let acting = null;
  try { acting = localStorage.getItem('bge_acting_as'); } catch { /* ignore */ }
  const primary = user?.primary_role || 'admin';
  if (!user || !acting || user.role === primary) return null;
  const isSuper = primary === 'admin' && (user.roles || []).length === 3;
  return (
    <div
      className="fixed bottom-3 left-1/2 -translate-x-1/2 z-[60] w-[calc(100%-1.5rem)] max-w-xl rounded-2xl bg-navy text-white shadow-2xl px-4 py-3 flex flex-wrap items-center gap-2"
      style={{ backgroundColor: '#001C50' }}
      role="status"
      data-testid="acting-banner"
    >
      <Repeat className="w-4 h-4 text-amber-300 flex-shrink-0" />
      <span className="text-sm flex-1 min-w-[10rem]" style={{ color: '#ffffff' }}>
        {isSuper ? 'Super admin · ' : ''}You&apos;re in the <strong>{ROLE_LABEL[user.role] || user.role}</strong> view
      </span>
      <button
        type="button"
        disabled={Boolean(switching)}
        onClick={() => switchTo(primary)}
        className="text-xs font-semibold rounded-full px-3 py-1.5 inline-flex items-center gap-1 hover:opacity-90 transition-opacity"
        style={{ backgroundColor: '#ffffff', color: '#001C50' }}
        data-testid="acting-back"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back to {isSuper && primary === 'admin' ? 'Super Admin' : ROLE_LABEL[primary]}
      </button>
    </div>
  );
};

export default ViewSwitcher;
